export const TEHRAN_TIMEZONE = 'Asia/Tehran';
export const TEHRAN_OFFSET_SECONDS = 12600; // UTC+03:30 (standard time across all year)

export interface TehranDateParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  dayOfWeek: number; // 0=Sunday, 1=Monday, ..., 6=Saturday
}

export function getTehranDateParts(dateInput: Date | number = Date.now()): TehranDateParts {
  const date =
    typeof dateInput === 'number'
      ? new Date(dateInput < 1e11 ? dateInput * 1000 : dateInput)
      : dateInput;
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: TEHRAN_TIMEZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    weekday: 'short',
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const get = (type: string) => {
    const val = parts.find((p) => p.type === type)?.value ?? '0';
    return parseInt(val, 10);
  };
  const weekdayStr = parts.find((p) => p.type === 'weekday')?.value ?? '';
  const weekdayMap: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  let hour = get('hour');
  if (hour === 24) hour = 0;
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour,
    minute: get('minute'),
    second: get('second'),
    dayOfWeek: weekdayMap[weekdayStr] ?? 0,
  };
}

export function nowUnix(now = Date.now()): number {
  return Math.floor(now / 1000);
}

export function startOfDayUnix(now = Date.now()): number {
  const p = getTehranDateParts(now);
  return Math.floor(Date.UTC(p.year, p.month - 1, p.day, 0, 0, 0) / 1000) - TEHRAN_OFFSET_SECONDS;
}

export function endOfDayUnix(now = Date.now()): number {
  return startOfDayUnix(now) + 86400 - 1;
}

/** Saturday-start week, matching Iranian institute calendars in Tehran timezone. */
export function startOfWeekUnix(now = Date.now()): number {
  const sod = startOfDayUnix(now);
  const p = getTehranDateParts(now);
  const daysFromSaturday = (p.dayOfWeek + 1) % 7;
  return sod - daysFromSaturday * 86400;
}

export function endOfWeekUnix(now = Date.now()): number {
  return startOfWeekUnix(now) + 7 * 86400 - 1;
}

export function persianYmd(unix: number = Math.floor(Date.now() / 1000)): { year: number; month: number; day: number } | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US-u-ca-persian', {
      timeZone: TEHRAN_TIMEZONE,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    }).formatToParts(new Date(unix * 1000));
    const num = (type: Intl.DateTimeFormatPartTypes) =>
      parseInt((parts.find((p) => p.type === type)?.value ?? '').replace(/\D/g, ''), 10);
    const year = num('year');
    const month = num('month');
    const day = num('day');
    if (!year || !month || !day) return null;
    return { year, month, day };
  } catch {
    return null;
  }
}

export function unixForPersian(year: number, month: number, day: number): number {
  let lo = Math.floor(Date.UTC(year + 620, 0, 1) / 1000);
  let hi = Math.floor(Date.UTC(year + 623, 0, 1) / 1000);
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    const p = persianYmd(mid);
    if (!p) return startOfDayUnix(mid * 1000);
    const cmp = p.year !== year ? p.year - year : p.month !== month ? p.month - month : p.day - day;
    if (cmp < 0) lo = mid + 1;
    else hi = mid;
  }
  return startOfDayUnix(lo * 1000);
}

export function daysInPersianMonth(year: number, month: number): number {
  const start = unixForPersian(year, month, 1);
  const next =
    month === 12 ? unixForPersian(year + 1, 1, 1) : unixForPersian(year, month + 1, 1);
  return Math.max(29, Math.round((next - start) / 86400));
}

export function startOfMonthUnix(now = Date.now()): number {
  const unix = typeof now === 'number' && now > 1e11 ? Math.floor(now / 1000) : now;
  const p = persianYmd(unix);
  if (!p) {
    const tp = getTehranDateParts(now);
    return Math.floor(Date.UTC(tp.year, tp.month - 1, 1, 0, 0, 0) / 1000) - TEHRAN_OFFSET_SECONDS;
  }
  return unixForPersian(p.year, p.month, 1);
}

export function endOfMonthUnix(now = Date.now()): number {
  const unix = typeof now === 'number' && now > 1e11 ? Math.floor(now / 1000) : now;
  const p = persianYmd(unix);
  if (!p) {
    const tp = getTehranDateParts(now);
    return Math.floor(Date.UTC(tp.year, tp.month, 1, 0, 0, 0) / 1000) - TEHRAN_OFFSET_SECONDS - 1;
  }
  const days = daysInPersianMonth(p.year, p.month);
  return unixForPersian(p.year, p.month, days) + 86400 - 1;
}

export type ScopePeriod = 'today' | 'week' | 'month';

export function resolveScopeBounds(period: ScopePeriod, now = Date.now()): { from: number; to: number } {
  if (period === 'today') return { from: startOfDayUnix(now), to: endOfDayUnix(now) };
  if (period === 'week') return { from: startOfWeekUnix(now), to: endOfWeekUnix(now) };
  return { from: startOfMonthUnix(now), to: endOfMonthUnix(now) };
}
